import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import AppModal from "../AppModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ModalStepOne from "./ModalStepOne";
import ModalStepTwo from "./ModalStepTwo";
import ModalStepThree from "./ModalStepThree";

const STEP_KEYS = ["upload", "mapping", "import"];

function ContactsImportModal({ open, onClose, onSave }) {
	const isMounted = useRef(true);
	const [activeStep, setActiveStep] = useState(0);
	const [selectedFile, setSelectedFile] = useState();
	const [contacts, setContacts] = useState([]);
	const [columns, setColumns] = useState({
		name: "",
		number: "",
		email: "",
	});
	const [loading, setLoading] = useState(false);

	const steps = STEP_KEYS.map((key) => i18n.t(`contactsImportModal.steps.${key}`));

	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[]
	);

	const resetState = () => {
		setContacts([]);
		setSelectedFile(undefined);
		setColumns({ name: "", number: "", email: "" });
		setActiveStep(0);
		setLoading(false);
	};

	const handleClose = () => {
		onClose();
		resetState();
	};

	const handleSaveContacts = async () => {
		setLoading(true);
		const sanitizedContacts = contacts.map((contact) => ({
			name: contact[columns.name],
			number: `+55${contact[columns.number].trim().replace(/\D/g, "")}`,
			email: contact[columns.email],
		}));

		try {
			await api.post("/contacts/bulk-create", { contacts: sanitizedContacts });
			if (!isMounted.current) return;
			toast.success(i18n.t("contactsImportModal.success"));
			onSave?.();
			handleClose();
		} catch (error) {
			toastError(error);
			if (isMounted.current) setLoading(false);
		}
	};

	const handleSelectedFile = (file) => {
	if (!file) return;
	setLoading(true);
	setSelectedFile(file);

	const reader = new FileReader();

	try {
		reader.onload = (e) => {
			const csvData = e.target.result;
			let normalizedCsv = csvData.replace(/\r/g, "");

			normalizedCsv += "\n";
			const lines = normalizedCsv.split("\n");

			const separator = lines[0].includes(";") ? ";" : ",";

			const headers = lines[0]
				.split(separator)
				.map((h) => h.trim());

			const result = [];

			for (let i = 1; i < lines.length; i++) {
				if (!lines[i].trim()) continue;

				const object = {};
				const currentLine = lines[i].split(separator);

				for (let j = 0; j < headers.length; j++) {
					object[headers[j]] = currentLine[j]?.trim() || "";
				}

				result.push(object);
			}

			setContacts(result);

			if (isMounted.current) setLoading(false);
		};

		reader.readAsText(file);
	} catch (error) {
		toastError(error);
		if (isMounted.current) setLoading(false);
	}
};

	const canGoToStep2 = Boolean(selectedFile && contacts.length > 0);
	const canGoToStep3 = columns.name !== "" && columns.number !== "";
	const headers = contacts[0] ? Object.keys(contacts[0]) : [];
	
	const renderFooter = () => {
		if (activeStep === 0) {
			return (
				<>
					<button type="button" className="btn btn-sub-gray" onClick={handleClose}>
						{i18n.t("contactsImportModal.buttons.cancel")}
					</button>
					<button
						type="button"
						className="btn btn-primary inline-flex items-center gap-2"
						disabled={!canGoToStep2 || loading}
						onClick={() => setActiveStep(1)}
					>
						{loading && <span className="app-modal-spinner !size-4 !border-2" />}
						{i18n.t("contactsImportModal.buttons.continue")}
					</button>
				</>
			);
		}

		if (activeStep === 1) {
			return (
				<>
					<button type="button" className="btn btn-sub-gray" onClick={() => setActiveStep(0)}>
						{i18n.t("contactsImportModal.buttons.back")}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={!canGoToStep3}
						onClick={() => setActiveStep(2)}
					>
						{i18n.t("contactsImportModal.buttons.continue")}
					</button>
				</>
			);
		}

		return (
			<>
				<button type="button" className="btn btn-sub-gray" onClick={() => setActiveStep(1)}>
					{i18n.t("contactsImportModal.buttons.back")}
				</button>
				<button
					type="button"
					className="btn btn-primary inline-flex items-center gap-2"
					disabled={!canGoToStep3 || loading}
					onClick={handleSaveContacts}
				>
					{loading && <span className="app-modal-spinner !size-4 !border-2" />}
					{i18n.t("contactsImportModal.buttons.import")}
				</button>
			</>
		);
	};

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title={i18n.t("contactsImportModal.title")}
			maxWidth={920}
			footer={renderFooter()}
		>
			<nav className="app-import-stepper" aria-label={i18n.t("contactsImportModal.title")}>
				{steps.map((label, index) => (
					<div
						key={STEP_KEYS[index]}
						className={[
							"app-import-step",
							index === activeStep && "is-active",
							index < activeStep && "is-done",
						]
							.filter(Boolean)
							.join(" ")}
					>
						<span className="app-import-step__index">{index + 1}</span>
						<span className="app-import-step__label">{label}</span>
					</div>
				))}
			</nav>

			{activeStep === 0 && (
				<ModalStepOne
					selectedFile={selectedFile}
					contactsLength={contacts.length}
					handleSelectedFile={handleSelectedFile}
					loading={loading}
				/>
			)}

			{activeStep === 1 && (
				<ModalStepTwo
					headers={headers}
					columns={columns}
					setColumns={setColumns}
				/>
			)}

			{activeStep === 2 && (
				<ModalStepThree
					columns={columns}
					contacts={contacts.slice(0, 4)}
				/>
			)}
		</AppModal>
	);
}

export default ContactsImportModal;
